import * as React from 'react'
import { Box, Typography, Chip, Stack, Menu, MenuItem, IconButton } from '@mui/material'
import { DashOutlined } from '@ant-design/icons'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router'
import { getActionItems } from 'utils/util'

import { useAuth } from 'services/hook/Auth'
import { useEventBusContext } from 'services/provider/EventBusProvider'

const STATUS_COLORS = {
    pending:   { bg: '#fff1b8', fg: '#d48806' },
    paid:      { bg: '#f6ffed', fg: '#389e0d' },
    failed:    { bg: '#fff2f0', fg: '#cf1322' },
    refunded:  { bg: '#e6f7ff', fg: '#096dd9' },
    cancelled: { bg: '#fff2f0', fg: '#cf1322' },
}

export const CellMobileDetails = ({ item, propsDetails }) => {
    const colors = STATUS_COLORS[item.status] ?? { bg: '#f5f5f5', fg: '#595959' }

    return (
        <Box sx={{ py: 1, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: '5px' }}>
                    <Typography variant="body">{`Transaction: `}</Typography>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {item.tran_ref ?? `#${item.id}`}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: '5px' }}>
                    <Typography variant="body">{`Customer: `}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {item.order?.customer?.name ?? '—'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: '5px' }}>
                    <Typography variant="body">{`Order: `}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {item.order?.order_number ?? '—'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: '5px' }}>
                    <Typography variant="body">{`Amount: `}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                        AED {parseFloat(item.amount ?? 0).toFixed(2)}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: '5px' }}>
                    <Typography variant="body">{`Status: `}</Typography>
                    <Chip
                        label={item.status}
                        size="small"
                        sx={{ backgroundColor: colors.bg, color: colors.fg, fontSize: '12px' }}
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <CellMobileActions item={item} actions={propsDetails?.actions} />
            </Box>
        </Box>
    )
}

/**
 * Generic reusable menu action item — supports event, onClick, or link (route navigation)
 */
const ActionMenuItem = ({ title, icon, event, payload, onClick, link, onClose }) => {
    const theme = useTheme()
    const eventbus = useEventBusContext()
    const navigate = useNavigate()
    const menuitemsx = { display: 'flex', alignItems: 'center', minHeight: 'auto', p: '8px 16px', '& .anticon': { color: theme.palette.primary.main, mr: '10px' } }

    const handleClick = (e) => {
        e.stopPropagation()

        if (onClick) {
            onClick(e, payload)
        } else if (link) {
            navigate(typeof link === 'function' ? link(payload) : link)
        } else {
            eventbus.fire(event, payload)
        }

        if (onClose) {
            onClose()
        }
    }

    return (
        <MenuItem sx={menuitemsx} onClick={handleClick}>
            {icon} {title}
        </MenuItem>
    )
}

/**
 * Render action menu dropdown with permission-based controls
 */
export const CellMobileActions = ({ item, actions }) => {
    const actionitems = getActionItems(item, actions)
    const { checkPermission } = useAuth()
    const [anchorEl, setAnchorEl] = React.useState(null)
    const open = Boolean(anchorEl)

    const handleClick = (e) => {
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <Stack direction="row" justifyContent="end">
            <IconButton
                id="payment-long-button"
                onClick={handleClick}
                sx={{ color: '#000', transform: 'rotate(90deg)' }}
            >
                <DashOutlined />
            </IconButton>

            <Menu
                id="payment-long-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        style: { maxHeight: 48 * 4.5, width: '16ch' }
                    }
                }}
            >
                {actionitems.map((action, idx) =>
                    checkPermission(action.permission) && (
                        <ActionMenuItem
                            key={idx}
                            title={action.menu}
                            icon={action.icon}
                            event={action.event}
                            payload={action.payload}
                            onClick={action.onClick}
                            link={action.link}
                            onClose={handleClose}
                        />
                    )
                )}
            </Menu>
        </Stack>
    )
}